/**
 * Presentation primitives shared by the sentinel-design panels, in the claims
 * area's existing idiom (theme.forecast tokens, small type, bordered cards) —
 * no new UI kit.
 */

import styled from 'styled-components'

export const Panel = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  /* Holds tables and auto-fill grids, so it must be allowed to be narrower
     than their min-content width rather than pushing past the pane. */
  min-width: 0;
  padding: 0.85rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 0.5rem;
  background: ${({ theme }) => theme.forecast.pageBackground1};
`

export const PanelTitle = styled.h3`
  margin: 0;
  font-size: 0.875rem;
`

export const PanelSubtitle = styled.h4`
  margin: 0.35rem 0 0;
  font-size: 0.78rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

export const Hint = styled.p`
  margin: 0;
  font-size: 0.75rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

export const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
`

export const ErrorText = styled.p`
  margin: 0;
  font-size: 0.78rem;
  white-space: pre-wrap;
  color: ${({ theme }) => theme.colors.error['400_base']};
`

/** Tone of a callout: neutral context, an advisory, or a hard stop. */
export type CalloutTone = 'neutral' | 'warning' | 'error'

const CALLOUT_ACCENT: Record<CalloutTone, string> = {
  neutral: 'transparent',
  warning: '#f97316',
  error: '#ef4444',
}

export const Callout = styled.div<{ $tone?: CalloutTone }>`
  padding: 0.5rem 0.7rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-left: 3px solid ${({ $tone = 'neutral' }) => CALLOUT_ACCENT[$tone]};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.background2};
  font-size: 0.75rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

/** Small inline chip for a status, count, or verdict. */
export const Badge = styled.span<{ $tone?: CalloutTone }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.1rem 0.4rem;
  border-radius: 0.25rem;
  border: 1px solid
    ${({ theme, $tone = 'neutral' }) =>
      $tone === 'neutral' ? theme.forecast.border : CALLOUT_ACCENT[$tone]};
  color: ${({ theme, $tone = 'neutral' }) =>
    $tone === 'neutral' ? theme.forecast.secondaryText : CALLOUT_ACCENT[$tone]};
  font-size: 0.68rem;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

export const StatTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.72rem;
  font-variant-numeric: tabular-nums;

  th,
  td {
    text-align: left;
    padding: 0.22rem 0.4rem;
    border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
    vertical-align: middle;
  }

  th {
    color: ${({ theme }) => theme.forecast.secondaryText};
    font-weight: 500;
    white-space: nowrap;
  }
`

export const ProgressLog = styled.div`
  max-height: 9rem;
  overflow-y: auto;
  padding: 0.4rem 0.5rem;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.pageBackground1};
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.7rem;
  white-space: pre-wrap;
`

export const List = styled.ul`
  margin: 0;
  padding-left: 1.1rem;
  font-size: 0.75rem;
  line-height: 1.5;

  li {
    margin-bottom: 0.2rem;
  }
`

/** Formats a [0, 1] fraction as a percentage, or an em dash when unknown. */
export const percent = (
  value: number | null | undefined,
  digits = 0
): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : `${(value * 100).toFixed(digits)}%`

/** Formats a number for a stats cell, or an em dash when unknown. */
export const num = (value: number | null | undefined, digits = 3): string =>
  value === null || value === undefined || !Number.isFinite(value)
    ? '—'
    : value.toFixed(digits)
